/**
 * Trocea texto largo en fragmentos ("chunks") para indexación de búsqueda.
 *
 * Los EPUBs pueden tener capítulos con miles de caracteres; guardarlos
 * enteros hace que los snippets de resultado sean poco útiles y que la
 * memoria del índice crezca. Partimos en trozos de tamaño controlado,
 * cortando preferentemente en límites de palabra para no partir términos.
 *
 * Módulo puro (sin I/O): testeable directamente.
 */

export interface Chunk {
  /** Índice ordinal del chunk dentro del texto original (0-based). */
  index: number
  /** Fragmento de texto (ya sin solapado). */
  text: string
}

/**
 * Parte `text` en chunks de ~`size` caracteres (por defecto 2000).
 *
 * El corte se realiza en el último límite de palabra (`\s`) que quepa dentro
 * de `size`, para no partir términos. Si una sola palabra excede `size`, se
 * corta forzosamente. Texto vacío → array vacío.
 */
export function chunkText(text: string, size = 2000): Chunk[] {
  const trimmed = text.trim()
  if (trimmed.length === 0) return []

  const chunks: Chunk[] = []
  let start = 0
  let i = 0

  while (start < trimmed.length) {
    let end = Math.min(start + size, trimmed.length)
    // Si no es el final del texto, buscar un límite de palabra hacia atrás.
    if (end < trimmed.length) {
      const slice = trimmed.slice(start, end)
      const lastSpace = slice.lastIndexOf(' ')
      if (lastSpace > 0) {
        end = start + lastSpace
      }
    }
    const chunk = trimmed.slice(start, end).trim()
    if (chunk.length > 0) {
      chunks.push({ index: i++, text: chunk })
    }
    start = end
    // Avanzar saltando el espacio en el que cortamos.
    while (start < trimmed.length && trimmed[start] === ' ') start++
  }

  return chunks
}
